import React, { useState, useReducer, useEffect, useCallback } from 'react';
import { styled, useTheme, useMediaQuery, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Fund } from '../api/useFunds';
import { CashbackModel, DEFAULT_BIC } from '../utils/cashbackConstants';
import { CashbackEstimationChart } from '../DetailCalculator/ChartSection/CashbackEstimationChart/CashbackEstimationChart';
import { toEuroWholeNumber } from '../utils/numberFormat';
import { PartnerBankSelector } from './PartnerBankSelector';
import { FundAutocomplete } from './FundAutocomplete';
import { FundInput } from './FundInput';
import { CashbackAdvantageBox } from './CashbackAdvantageBox';
import { CashbackComparisonChart } from './CashbackComparisonChart';
import { AggregatedCommissionBox } from './AggregatedCommissionBox';
import { CashbackDisclaimer } from './CashbackDisclaimer';
import { calculateCashback } from './cashbackCalculatorUtil';

const FlexContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap-reverse',
});

const AutocompleteContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  [theme.breakpoints.only('xs')]: {
    flexWrap: 'wrap',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const ChartContainer = styled('div')(({ theme }) => ({
  height: '300px',
  width: '600px',
  [theme.breakpoints.only('xs')]: {
    width: '100%',
    alignSelf: 'flex-end',
    marginBottom: '30px',
  },
}));

const WhiteBackgroundWithSpacing = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  [theme.breakpoints.only('xs')]: {
    padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
  },
}));

const ClearAll = styled(Typography)(({ theme }) => ({
  color: theme.palette.action.active,
  cursor: 'pointer',
  textAlign: 'right',
}));

export interface CashbackAdvantage {
  cashback: number;
  totalPortfolioCommission: number;
  totalAgio: number;
  fee: number;
}

interface AddAction {
  type: 'add';
  payload: {
    fund: Fund;
  };
}

interface RemoveAction {
  type: 'remove';
  payload: {
    fund: Fund;
  };
}

interface UpdateAction {
  type: 'update';
  payload: {
    fund: Fund;
    investedAmount: number;
  };
}

interface ClearAction {
  type: 'clear';
}

const reducer = (
  state: Map<Fund, number>,
  action: AddAction | RemoveAction | UpdateAction | ClearAction
) => {
  switch (action.type) {
    case 'add': {
      return !action.payload?.fund || state.has(action.payload.fund)
        ? state
        : new Map(state.set(action.payload.fund, 10000));
    }
    case 'remove': {
      state.delete(action.payload.fund);
      return new Map(state);
    }
    case 'update': {
      return state.has(action.payload.fund)
        ? new Map(state.set(action.payload.fund, action.payload.investedAmount))
        : state;
    }
    case 'clear': {
      return new Map<Fund, number>();
    }

    default:
      throw new Error('invalid dispatch state given');
  }
};

export interface Props {
  initialState?: Map<Fund, number>;
  initialBic?: Bic;
  onStateChange?: (state: Map<Fund, number>, bic: Bic) => void;
  onFundSelect?: (fund: Fund) => void;
  bookNowUrl: string;
  model?: CashbackModel;
}

export const DetailCalculator = ({
  initialState = new Map(),
  initialBic,
  onStateChange,
  onFundSelect,
  bookNowUrl,
  model = 'flat-fee',
}: Props) => {
  const [fundsMap, dispatch] = useReducer(reducer, initialState);
  const [showFundAutocomplete, setShowFundAutocomplete] = useState<boolean>(true);
  const [bic, setBic] = useState<Bic>(initialBic ? initialBic : DEFAULT_BIC);
  const [showCashbackAdvantageBox, setShowCashbackAdvantageBox] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
  const { t } = useTranslation();

  useEffect(() => {
    if (onStateChange) {
      onStateChange(fundsMap, bic);
    }
  }, [fundsMap, bic, onStateChange]);

  const handlePartnerBankChange = (bic: Bic) => {
    setBic(bic);
  };

  const handleFundSelection = (fund: Fund) => {
    if (onFundSelect) {
      onFundSelect(fund);
    }
    dispatch({ type: 'add', payload: { fund: fund } });
    setShowFundAutocomplete(!fund || !!fundsMap.get(fund));
  };

  const handleFundRemoval = (fund: Fund) => {
    dispatch({ type: 'remove', payload: { fund: fund } });
    setShowFundAutocomplete(true);
  };

  const handleInvestedAmountChange = (fund: Fund, investedAmount: number) => {
    dispatch({
      type: 'update',
      payload: { fund: fund, investedAmount: investedAmount },
    });

    setShowFundAutocomplete(!!investedAmount);
  };

  const cashbackAdvantage = calculateCashback(fundsMap, bic, model);

  const handleCalculateNowButtonClick = () => {
    setShowCashbackAdvantageBox(true);
  };

  const handleBackButtonClick = () => {
    setShowCashbackAdvantageBox(false);
  };

  const handleBarChartClick = useCallback(
    (bic: Bic) => {
      setBic(bic);
    },
    [setBic]
  );

  return (
    <>
      <WhiteBackgroundWithSpacing>
        <PartnerBankSelector onChange={handlePartnerBankChange} bic={bic} />
      </WhiteBackgroundWithSpacing>

      {fundsMap.size > 0 && (
        <>
          {Array.from(fundsMap).map(([fund, investedAmount], index) => {
            return (
              <WhiteBackgroundWithSpacing key={`bg_${fund.isin}`}>
                <FundInput
                  key={`${fund.isin}-${index}`}
                  testId={`fund-input-${fund.isin}`}
                  bic={bic}
                  fund={fund}
                  isCollapsed={isMobile && fundsMap.size - 1 !== index}
                  investedAmount={investedAmount}
                  onChange={handleInvestedAmountChange}
                  onRemove={handleFundRemoval}
                />
              </WhiteBackgroundWithSpacing>
            );
          })}
        </>
      )}

      {showFundAutocomplete && (
        <WhiteBackgroundWithSpacing>
          <AutocompleteContainer>
            <FundAutocomplete
              id="fund-autocomplete"
              autocompleteKey={fundsMap.size.toString()} // this will clear the autocomplete once the length changes
              showAddIcon={fundsMap.size > 0}
              onChange={handleFundSelection}
            />
            {fundsMap.size > 0 && (
              <ClearAll
                variant="body2"
                onClick={() => {
                  dispatch({ type: 'clear' });
                }}
              >
                {t('cashbackCalculator.detailed.calculator.clearAll')}
              </ClearAll>
            )}
          </AutocompleteContainer>
        </WhiteBackgroundWithSpacing>
      )}

      {!showCashbackAdvantageBox && cashbackAdvantage.cashback > 0 && (
        <div>
          <WhiteBackgroundWithSpacing>
            <FlexContainer>
              <ChartContainer>
                <Typography variant="h5">
                  {t('cashbackCalculator.detailed.calculator.chartHeading')}
                </Typography>
                <CashbackComparisonChart
                  fundsMap={fundsMap}
                  bic={bic}
                  onClick={handleBarChartClick}
                />
              </ChartContainer>
              <div>
                <AggregatedCommissionBox
                  cashbackAdvantage={cashbackAdvantage}
                  bookNowUrl={bookNowUrl}
                  handleCalculateNowButtonClick={handleCalculateNowButtonClick}
                />
              </div>
            </FlexContainer>
          </WhiteBackgroundWithSpacing>
        </div>
      )}

      {showCashbackAdvantageBox && (
        <WhiteBackgroundWithSpacing>
          <FlexContainer>
            <ChartContainer>
              <Typography variant="h5">
                {t('cashbackCalculator.detailed.cashbackAreaChart.chartHeading', {
                  depotVolume: toEuroWholeNumber(cashbackAdvantage.totalInvestedAmount),
                })}
              </Typography>
              <CashbackEstimationChart
                wealth={cashbackAdvantage.totalInvestedAmount}
                portfolioCommissionPercent={cashbackAdvantage.totalPortfolioCommissionInPercent}
                model={model}
              />
            </ChartContainer>
            <div>
              <CashbackAdvantageBox
                bookNowUrl={bookNowUrl}
                cashbackAdvantage={cashbackAdvantage}
                handleBackButtonClick={handleBackButtonClick}
              />
            </div>
          </FlexContainer>
        </WhiteBackgroundWithSpacing>
      )}

      {fundsMap.size > 0 && (
        <WhiteBackgroundWithSpacing>
          <CashbackDisclaimer />
        </WhiteBackgroundWithSpacing>
      )}
    </>
  );
};

export default DetailCalculator;
